/* Lynk painted Christmas journey. Native scrolling is the only timeline. */
(function () {
  'use strict';
  if (window.LynkVisualV14) return;

  const selector = '[data-lynk-v14]';
  const instances = new Map();
  const scenes = new Map();
  const houseModules = new Map();
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initialMotion = document.documentElement.classList.contains('lynk-v14-motion')
    && document.documentElement.dataset.v14MotionEligible === 'true';
  const scents = new Set(['white-tea', 'firlight-noel', 'christmas-eve-latte']);
  const phases = ['opening', 'making', 'flight', 'homes', 'mum'];
  const phaseEdges = [0, 0.16, 0.30, 0.45, 0.94, 1];
  let frame = 0;
  let frames = 0;
  let staticControlOwner = null;

  const clamp = value => Math.max(0, Math.min(1, value));
  const ease = (start, end, value) => {
    const t = clamp((value - start) / (end - start));
    return t * t * (3 - 2 * t);
  };
  const lerp = (start, end, progress) => start + (end - start) * progress;
  const round = value => Math.round(value * 10000) / 10000;
  const canPaint = item => item.running && item.visible && !document.hidden;

  function requestPaint() {
    if (!frame && Array.from(instances.values()).some(canPaint)) {
      frame = window.requestAnimationFrame(paint);
    }
  }
  function stopUnusedFrame() {
    if (frame && !Array.from(instances.values()).some(canPaint)) {
      window.cancelAnimationFrame(frame);
      frame = 0;
    }
  }
  function updateStaticControls() {
    const candidates = Array.from(instances.values()).filter(item => item.visible
      && item.root.dataset.v14Motion === 'static' && item.root.dataset.v14Resume === 'true');
    if (!candidates.includes(staticControlOwner)) staticControlOwner = candidates[0] || null;
    instances.forEach(item => {
      if (item === staticControlOwner) item.root.dataset.v14ControlsActive = 'true';
      else delete item.root.dataset.v14ControlsActive;
    });
  }
  function paint() {
    frame = 0;
    if (document.hidden) return;
    frames += 1;
    const y = window.scrollY || 0;
    instances.forEach(item => {
      if (!canPaint(item)) return;
      const measured = item.dirty;
      if (measured) item.measure(y);
      item.paint(clamp((y - item.top) / item.travel), measured);
    });
    // A scroll/resize/observer event wakes the next frame. There is no idle loop.
  }

  const observer = typeof window.IntersectionObserver === 'function'
    ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const item = scenes.get(entry.target);
        if (!item) return;
        item.visible = entry.isIntersecting;
        item.houseVisibility();
        if (item.visible) item.dirty = true;
      });
      updateStaticControls();
      stopUnusedFrame();
      requestPaint();
    }, { threshold: 0 }) : null;

  function selectedScent(root, key) {
    const finder = root.querySelector('[data-gift-finder]');
    if (!scents.has(key) && !finder) return;
    // Collection stories remain under the core controller's visibility rules.
    finder?.querySelectorAll('[data-v14-scent-scene]').forEach(scene => {
      const chosen = scene.dataset.v14ScentScene === key;
      scene.hidden = !chosen;
      scene.setAttribute('aria-hidden', String(!chosen));
    });
    if (scents.has(key)) root.dataset.v14Scent = key;
    else delete root.dataset.v14Scent;
  }
  function syncScent(root) {
    const finder = root.querySelector('[data-gift-finder]');
    if (!finder) { selectedScent(root, root.dataset.currentScent || 'white-tea'); return; }
    const state = finder.lynkGiftFinderApi?.getState();
    const key = root.lynkChristmasApi?.data?.feelings?.[state?.feeling]?.scent;
    selectedScent(root, key || null);
  }

  function initialize(root) {
    syncScent(root);
    if (instances.has(root)) return instances.get(root).api;
    const scene = root.querySelector('[data-v14-scene]');
    const stage = root.querySelector('[data-v14-stage]');
    if (!scene || !stage) return null;
    const toggle = root.querySelector('[data-v14-motion-toggle]');
    const beats = Array.from(root.querySelectorAll('[data-v14-beat]'));
    const houseMount = root.querySelector('[data-v15-house-world]');
    const criticalImages = Array.from(root.querySelectorAll('[data-v14-critical]'));
    const styleValues = new Map();
    const signal = new AbortController();
    let assetFailed = false;
    let assetsReady = criticalImages.length === 0;
    let paused = false;
    try { paused = ['true', '1'].includes(localStorage.getItem('lynk-xmas-static')); } catch (_) {}
    const eligible = !!observer;
    let motionView = initialMotion && !paused;
    let orientation = null;
    let houseScene = null;
    let houseReady = false;
    let housePending = false;
    let houseGeneration = 0;
    let houseTask = 0;
    let idleTask = false;
    let houseDisposing = false;
    const item = {
      root, scene, stage, eligible, running: motionView && !media.matches,
      visible: false, dirty: true, mobile: null, top: 0, travel: 1, progress: null, phase: '', paints: 0, measures: 0,
      measure(y) {
        const rect = scene.getBoundingClientRect();
        const mobile = window.innerWidth <= 640;
        const mobileChanged = item.mobile !== mobile;
        item.mobile = mobile;
        item.top = rect.top + y;
        item.travel = Math.max(1, rect.height - stage.getBoundingClientRect().height);
        houseScene?.resize();
        item.dirty = false;
        item.measures += 1;
        return mobileChanged;
      },
      paint(progress, force = false) {
        // Prewarm after the opening starts. A fast shopping bypass never waits.
        if (progress >= 0.08 && progress < 0.94) ensureHouse();
        if (assetFailed) return;
        if (!assetsReady) progress = Math.min(progress, 0.30);
        if (!houseReady && progress > 0.285 && progress < 0.94) progress = 0.285;
        if (!force && item.progress !== null && Math.abs(progress - item.progress) < 0.0001) return;
        item.progress = progress;
        item.paints += 1;
        const openingOut = ease(0.12, 0.25, progress);
        const makingIn = ease(0.13, 0.22, progress);
        const makingOut = ease(0.27, 0.32, progress);
        const housesIn = ease(0.275, 0.32, progress);
        const mumIn = ease(0.94, 0.995, progress);
        const worldProgress = clamp((progress - 0.30) / 0.62);
        const departure = ease(0.365, 0.43, progress);
        const set = (name, value) => {
          const text = typeof value === 'number' ? String(round(value)) : value;
          if (styleValues.get(name) !== text) {
            scene.style.setProperty(name, text);
            styleValues.set(name, text);
          }
        };
        set('--v14-opening-opacity', 1 - openingOut);
        set('--v14-opening-scale', lerp(1, 1.35, ease(0, 0.26, progress)));
        set('--v14-opening-x', '0vw');
        set('--v14-opening-y', round(lerp(0, 3, openingOut)) + 'vh');
        set('--v14-making-opacity', makingIn * (1 - makingOut));
        set('--v14-making-scale', lerp(1.06, 1, ease(0.13, 0.30, progress)));
        set('--v15-houses-opacity', housesIn * (1 - mumIn));
        set('--v15-poster-opacity', houseReady ? 0 : housesIn * (1 - mumIn));
        // Both viewpoints share these physical houses; the camera keeps travelling.
        if (houseReady && houseScene) {
          houseScene.setVisible(canPaint(item) && progress >= 0.27 && progress < 0.995);
          houseScene.setProgress(worldProgress);
          scene.dataset.v15WorldProgress = String(round(worldProgress));
        }
        set('--v14-sleigh-opacity', ease(0.30, 0.335, progress) * (1 - ease(0.44, 0.45, progress)));
        set('--v14-sleigh-x', round(lerp(0, 150, departure)) + 'vw');
        set('--v14-sleigh-y', round(lerp(0, -75, departure)) + 'vh');
        set('--v14-sleigh-rotate', round(lerp(0, -12, departure)) + 'deg');
        set('--v14-sleigh-scale', lerp(0.95, 0.55, departure));
        set('--v14-pov-opacity', ease(0.44, 0.47, progress) * (1 - ease(0.93, 0.97, progress)));
        set('--v14-pov-scale', lerp(0.97, 1.04, ease(0.44, 0.92, progress)));
        set('--v14-pov-y', '0vh');
        set('--v14-pov-bank', '0deg');
        set('--v14-mum-opacity', mumIn);
        set('--v14-mum-scale', lerp(1.08, 1, mumIn));
        const phase = phases[progress < phaseEdges[1] ? 0 : progress < phaseEdges[2] ? 1 : progress < phaseEdges[3] ? 2 : progress < phaseEdges[4] ? 3 : 4];
        if (phase !== item.phase || force) {
          item.phase = phase;
          scene.dataset.v14Phase = phase;
          beats.forEach(beat => {
            const active = beat.dataset.v14Beat === phase;
            beat.hidden = !active;
            beat.inert = !active;
            beat.style.setProperty('--v14-beat-opacity', active ? '1' : '0');
          });
        }
        scene.dataset.v14Progress = String(round(progress));
      },
      houseVisibility() {
        if (!canPaint(item)) {
          if (housePending && !houseReady) disposeHouse();
          else houseScene?.setVisible(false);
        }
      },
      api: null
    };
    function disposeHouse() {
      houseGeneration += 1;
      if (houseTask) {
        if (idleTask) window.cancelIdleCallback(houseTask);
        else window.clearTimeout(houseTask);
      }
      houseTask = 0;
      housePending = false;
      houseReady = false;
      houseDisposing = true;
      try { houseScene?.destroy(); } finally { houseDisposing = false; }
      houseScene = null;
      if (houseMount) delete houseMount.dataset.ready;
      delete scene.dataset.v15WorldProgress;
    }
    function ensureHouse() {
      if (houseReady || housePending || !canPaint(item) || assetFailed) return;
      const url = houseMount?.dataset.module;
      if (!url) { fallbackToStatic(); return; }
      housePending = true;
      const token = ++houseGeneration;
      const current = () => token === houseGeneration && !signal.signal.aborted
        && item.running && item.visible && !document.hidden && !assetFailed;
      if (!houseModules.has(url)) houseModules.set(url, import(url));
      houseModules.get(url).then(module => {
        if (!current()) { if (token === houseGeneration) disposeHouse(); return; }
        const create = () => {
          houseTask = 0;
          if (!current()) { if (token === houseGeneration) disposeHouse(); return; }
          let candidate;
          try {
            candidate = module.createHouseFlightScene(houseMount, {
              backgroundUrl: houseMount.dataset.background,
              materialAtlasUrl: houseMount.dataset.atlas,
              externallyControlled: true
            });
            houseScene = candidate;
            candidate.setVisible(false);
          } catch (_) { fallbackToStatic(); return; }
          Promise.resolve(candidate.ready).then(() => {
            if (!current() || houseScene !== candidate) {
              candidate.destroy();
              if (token === houseGeneration) disposeHouse();
              return;
            }
            housePending = false;
            houseReady = true;
            houseMount.dataset.ready = 'true';
            item.progress = null;
            item.dirty = true;
            requestPaint();
          }, () => {
            candidate.destroy();
            if (token === houseGeneration && !signal.signal.aborted) fallbackToStatic();
          });
        };
        idleTask = typeof window.requestIdleCallback === 'function';
        houseTask = idleTask ? window.requestIdleCallback(create, { timeout: 300 })
          : window.setTimeout(create, 0);
      }, () => {
        if (token === houseGeneration && !signal.signal.aborted) fallbackToStatic();
      });
    }
    function clearMotionStyles() {
      beats.forEach(beat => { beat.hidden = false; beat.inert = false; beat.style.removeProperty('--v14-beat-opacity'); });
      styleValues.forEach((_, key) => scene.style.removeProperty(key));
      styleValues.clear();
      delete scene.dataset.v14Progress;
      delete scene.dataset.v14Phase;
    }
    function staticOrientation() {
      const reference = Math.min(180, window.innerHeight * 0.25);
      let chosen = beats[0], distance = Infinity;
      beats.forEach(beat => {
        const rect = beat.getBoundingClientRect();
        const away = rect.top > reference ? rect.top - reference : rect.bottom < reference ? reference - rect.bottom : 0;
        if (away < distance) { chosen = beat; distance = away; }
      });
      if (!chosen) return { progress: 0, phase: 'opening', offset: 0 };
      const phase = chosen.dataset.v14Beat;
      if (orientation?.phase === phase) return orientation;
      const index = Math.max(0, phases.indexOf(phase));
      const edges = phaseEdges;
      const rect = chosen.getBoundingClientRect();
      const fraction = clamp((reference - rect.top) / Math.max(1, rect.height));
      return { phase, progress: Math.min(edges[index + 1] - 0.002,
        Math.max(edges[index] + 0.002, lerp(edges[index], edges[index + 1], fraction))), offset: 0 };
    }
    function refreshMode() {
      item.running = motionView && !media.matches && !assetFailed;
      if (!item.running) {
        // A live OS preference freezes the current view without changing geometry.
        // Initial reduced motion and explicit Still use the ordinary-flow chapters.
        if (motionView && media.matches && houseReady) houseScene?.setVisible(false);
        else disposeHouse();
      }
      root.dataset.v14Motion = motionView ? (item.running ? 'active' : 'still') : 'static';
      const canResume = !motionView && eligible && !media.matches && !assetFailed;
      if (canResume) root.dataset.v14Resume = 'true';
      else delete root.dataset.v14Resume;
      if (toggle) {
        toggle.hidden = !eligible || assetFailed;
        toggle.disabled = media.matches || !eligible || assetFailed;
        toggle.setAttribute('aria-pressed', String(!item.running));
        toggle.textContent = media.matches
          ? (toggle.dataset.v14LabelReduced || 'Reduced motion')
          : item.running ? (toggle.dataset.v14LabelStill || 'Still view')
            : (toggle.dataset.v14LabelMotion || 'Enable motion');
      }
      if (motionView && item.progress === null) {
        item.measure(window.scrollY || 0);
        item.paint(clamp(((window.scrollY || 0) - item.top) / item.travel), true);
      }
      updateStaticControls();
      if (item.running) { item.dirty = true; requestPaint(); }
      else stopUnusedFrame();
    }
    toggle?.addEventListener('click', () => {
      if (!eligible || media.matches || assetFailed) return;
      if (motionView) {
        item.measure(window.scrollY || 0);
        item.paint(clamp(((window.scrollY || 0) - item.top) / item.travel), true);
        const beat = beats.find(node => node.dataset.v14Beat === item.phase) || beats[0];
        orientation = { progress: item.progress, phase: item.phase,
          offset: Math.max(0, Math.min(window.innerHeight * 0.5, beat?.getBoundingClientRect().top || 0)) };
        motionView = false;
        paused = true;
        try { localStorage.setItem('lynk-xmas-static', 'true'); } catch (_) {}
        item.running = false;
        clearMotionStyles();
        refreshMode();
        // Only an explicit mode click anchors the corresponding ordinary-flow chapter.
        if (beat) window.scrollTo({ top: beat.getBoundingClientRect().top + window.scrollY - orientation.offset, behavior: 'instant' });
      } else {
        const next = staticOrientation();
        orientation = next;
        paused = false;
        try { localStorage.removeItem('lynk-xmas-static'); } catch (_) {}
        // Explicit Resume may reserve motion geometry for this visit; it never reloads.
        document.documentElement.classList.add('lynk-v14-motion');
        motionView = true;
        refreshMode();
        item.measure(window.scrollY || 0);
        item.paint(next.progress, true);
        window.scrollTo({ top: item.top + item.travel * next.progress, behavior: 'instant' });
        item.dirty = true;
        requestPaint();
      }
    }, { signal: signal.signal });
    function fallbackToStatic() {
      if (assetFailed || signal.signal.aborted) return;
      assetFailed = true;
      assetsReady = false;
      root.dataset.v14Assets = 'failed';
      // Preserve an already focused shopping destination if loading fails after a skip.
      const destination = document.activeElement;
      const keepDestination = root.contains(destination) && destination.matches?.('.lc-catalog-destination');
      const beforeTop = keepDestination ? destination.getBoundingClientRect().top : 0;
      motionView = false;
      item.running = false;
      disposeHouse();
      clearMotionStyles();
      refreshMode();
      if (keepDestination) window.scrollBy({ top: destination.getBoundingClientRect().top - beforeTop, behavior: 'instant' });
    }
    function checkAssets() {
      if (assetFailed || signal.signal.aborted) return;
      if (criticalImages.some(image => image.complete && !image.naturalWidth)) { fallbackToStatic(); return; }
      assetsReady = criticalImages.every(image => image.complete && image.naturalWidth > 0);
      root.dataset.v14Assets = assetsReady ? 'ready' : 'loading';
      if (assetsReady) requestPaint();
    }
    criticalImages.forEach(image => {
      image.addEventListener('error', fallbackToStatic, { once: true, signal: signal.signal });
      image.addEventListener('load', checkAssets, { once: true, signal: signal.signal });
    });
    houseMount?.addEventListener('webglcontextlost', event => {
      if (houseDisposing || !houseScene || event.target !== houseMount.querySelector('canvas')) return;
      event.preventDefault();
      fallbackToStatic();
    }, { capture: true, signal: signal.signal });
    const scentObserver = new MutationObserver(() => selectedScent(root, root.dataset.currentScent));
    scentObserver.observe(root, { attributes: true, attributeFilter: ['data-current-scent'] });
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
      item.dirty = true;
      requestPaint();
    }) : null;
    [root, scene, stage].forEach(node => resizeObserver?.observe(node));
    item.api = {
      getMetrics: () => ({ motion: item.running, visible: item.visible, progress: item.progress, phase: item.phase, paints: item.paints, measures: item.measures, pendingFrame: !!frame, assets: root.dataset.v14Assets,
        house: { ready: houseReady, pending: housePending, generation: houseGeneration, scene: houseScene?.getMetrics() || null } }),
      destroy() {
        signal.abort();
        disposeHouse();
        observer?.unobserve(scene);
        resizeObserver?.disconnect();
        scentObserver.disconnect();
        scenes.delete(scene);
        instances.delete(root);
        root.dataset.v14Motion = 'static';
        delete root.dataset.v14Resume;
        delete root.dataset.v14Assets;
        clearMotionStyles();
        delete root.dataset.v14ControlsActive;
        updateStaticControls();
        if (toggle) toggle.hidden = true;
        if (root.lynkVisualV14 === item.api) delete root.lynkVisualV14;
        stopUnusedFrame();
      }
    };
    // Media listeners use the same instance lifetime as all other controls.
    media.addEventListener('change', refreshMode, { signal: signal.signal });
    instances.set(root, item);
    scenes.set(scene, item);
    root.lynkVisualV14 = item.api;
    checkAssets();
    refreshMode();
    observer?.observe(scene);
    return item.api;
  }
  function initializeIn(container) {
    if (container.matches?.(selector)) initialize(container);
    container.querySelectorAll?.(selector).forEach(initialize);
  }
  function destroyIn(container) {
    Array.from(instances).forEach(([root, item]) => {
      if (container === root || container.contains?.(root)) item.api.destroy();
    });
  }
  window.LynkVisualV14 = {
    init: initializeIn, destroy: destroyIn,
    getMetrics: () => ({ instances: instances.size, frames, pendingFrame: !!frame })
  };
  // A single passive listener wakes a single frame for every visible instance.
  window.addEventListener('scroll', requestPaint, { passive: true });
  window.addEventListener('resize', () => { instances.forEach(item => { item.dirty = true; }); requestPaint(); }, { passive: true });
  document.addEventListener('visibilitychange', () => {
    instances.forEach(item => item.houseVisibility());
    if (document.hidden) stopUnusedFrame();
    else { instances.forEach(item => { item.dirty = true; }); requestPaint(); }
  });
  document.addEventListener('lynk:ready', event => {
    initializeIn(event.target);
    const root = event.target.closest?.(selector);
    if (root) syncScent(root);
  });
  document.addEventListener('lynk:gift-finder-stage', event => {
    const root = event.target.closest?.(selector);
    if (root) selectedScent(root, event.detail?.scent);
  });
  document.addEventListener('shopify:section:load', event => initializeIn(event.target));
  document.addEventListener('shopify:section:unload', event => destroyIn(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeIn(document), { once: true });
  else initializeIn(document);
}());
